import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
  BanknotesIcon,
  ShoppingCartIcon,
  HeartIcon,
  ShieldCheckIcon,
  EyeIcon,
  ArrowRightIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';

const SystemOverview = () => {
  const navigate = useNavigate();

  const roles = [
    {
      name: 'Service Advisor',
      icon: UserGroupIcon,
      color: 'blue',
      responsibilities: [
        'Customer onboarding and management',
        'Vehicle registration and documentation',
        'Job card/DQ creation',
        'Task assignment to mechanics',
        'Work order management',
        'Customer communication',
      ],
      keyFeatures: [
        'Customer Management (CRUD operations)',
        'Vehicle Management (full access)',
        'Job Card Creation & Management',
        'Task Assignment',
        'Photo Documentation',
        'Vehicle Inspection',
        'Appointment Scheduling',
      ],
    },
    {
      name: 'Mechanic',
      icon: WrenchScrewdriverIcon,
      color: 'green',
      responsibilities: [
        'Execute assigned tasks',
        'Document work progress',
        'Report issues and findings',
        'Complete repairs per specifications',
        'Maintain quality and safety standards',
      ],
      keyFeatures: [
        'Task Dashboard (view assigned tasks)',
        'Start/complete tasks',
        'Add notes and observations',
        'Upload work photos',
        'View job card details',
        'Access vehicle history',
      ],
    },
    {
      name: 'Finance',
      icon: BanknotesIcon,
      color: 'yellow',
      responsibilities: [
        'Quotation creation and management',
        'Invoice generation and tracking',
        'Receivables and payables management',
        'Commission tracking',
        'Tax management',
        'Financial reporting',
      ],
      keyFeatures: [
        'Create/edit quotations',
        'Generate invoices',
        'Track receivables',
        'Manage payables',
        'Commission management',
        'Tax tracking',
        'Financial analytics',
        'HR dashboard (leave management)',
      ],
    },
    {
      name: 'Procurement',
      icon: ShoppingCartIcon,
      color: 'purple',
      responsibilities: [
        'Job card pricing (add cost prices)',
        'Inventory management',
        'Supplier management',
        'Purchase requisitions',
        'Delivery tracking',
        'Margin configuration',
      ],
      keyFeatures: [
        'Job Card Pricing (cost entry)',
        'Inventory Management',
        'Supplier Registry',
        'Create Requisitions',
        'Approve Purchases',
        'Update Delivery Status',
        'Margin Settings',
      ],
    },
    {
      name: 'Customer Care',
      icon: HeartIcon,
      color: 'pink',
      responsibilities: [
        'Customer relationship management',
        'Feedback collection and management',
        'Complaint resolution',
        'Appointment scheduling',
        'Customer analytics',
        'Birthday tracking',
      ],
      keyFeatures: [
        'Customer Management (full CRUD)',
        'Feedback Management',
        'Complaint Tracking',
        'Appointment Scheduling',
        'Customer Analytics',
        'Birthday Lists',
      ],
    },
    {
      name: 'Admin',
      icon: ShieldCheckIcon,
      color: 'red',
      responsibilities: [
        'User management (create employees)',
        'Role-based access control (RBAC)',
        'System configuration',
        'Service/product management',
        'Contractor management',
        'Help center management',
      ],
      keyFeatures: [
        'Employee Management (create/edit users)',
        'RBAC Configuration',
        'Contractor Registry',
        'Service Catalog',
        'Referral Agent Management',
        'Help Article Management',
        'System Settings',
      ],
    },
    {
      name: 'Supervisor',
      icon: EyeIcon,
      color: 'indigo',
      responsibilities: [
        'Team oversight',
        'Task monitoring',
        'Performance tracking',
        'Quality assurance',
        'Task verification',
        'Resource allocation',
      ],
      keyFeatures: [
        'Task Dashboard (all tasks)',
        'Mechanic Performance Metrics',
        'Task Verification',
        'Team Calendar',
        'Job Oversight',
        'Performance Analytics',
      ],
    },
  ];

  const workflows = [
    {
      title: 'Job Card Lifecycle',
      steps: [
        'Customer Arrival',
        'Service Advisor: Create Job Card/DQ',
        'Status: Open + Approval: Pending',
        'Manager: Approve',
        'Approval Status: Yes',
        'Service Advisor: Assign Tasks to Mechanics',
        'Procurement: Add Cost Prices',
        'Mechanic: Execute Tasks',
        'Supervisor: Verify Completed Work',
        'Finance: Create Quotation (with margins)',
        'Customer: Approve Quotation',
        'Mechanic: Complete Remaining Work',
        'Service Advisor: Final Inspection',
        'Finance: Generate Invoice',
        'Customer: Payment',
        'Finance: Update Receivables',
        'Service Advisor: Close Job Card',
        'Status: Closed',
        'Customer Care: Follow-up Call',
      ],
    },
    {
      title: 'Purchase Workflow',
      steps: [
        'Job Card Created with Parts List',
        'Procurement: Check Inventory',
        'If Not In Stock: Create Requisition',
        'Select Supplier',
        'Manager: Approve Requisition',
        'Procurement: Place Order',
        'Supplier: Ship Parts',
        'Procurement: Update Delivery Status',
        'Parts Received',
        'Add to Inventory OR Allocate to Job',
        'Finance: Update Payables',
      ],
    },
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      yellow: 'bg-yellow-100 text-yellow-600',
      purple: 'bg-purple-100 text-purple-600',
      pink: 'bg-pink-100 text-pink-600',
      red: 'bg-red-100 text-red-600',
      indigo: 'bg-indigo-100 text-indigo-600',
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate('/help')}
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Back to Help Center
            </button>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ClipboardDocumentListIcon className="h-16 w-16 text-white mx-auto mb-4" />
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            System Overview
          </h1>
          <p className="text-lg text-blue-100 max-w-3xl mx-auto">
            Understanding roles, responsibilities, and workflows in the Ayden Garage Management System
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Roles Section */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">System Roles</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {roles.map((role, index) => {
              const Icon = role.icon;
              return (
                <div key={index} className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-center mb-4">
                    <div className={`p-3 rounded-lg ${getColorClasses(role.color)} mr-4`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">{role.name}</h3>
                  </div>

                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                      Primary Responsibilities:
                    </h4>
                    <ul className="space-y-1.5">
                      {role.responsibilities.map((resp, idx) => (
                        <li key={idx} className="text-sm text-gray-600 flex items-start">
                          <span className="text-blue-600 mr-2">•</span>
                          <span>{resp}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                      Key Features Access:
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {role.keyFeatures.slice(0, 4).map((feature, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {feature}
                        </span>
                      ))}
                      {role.keyFeatures.length > 4 && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                          +{role.keyFeatures.length - 4} more
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Workflows Section */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Workflows</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {workflows.map((workflow, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {workflow.title}
                </h3>
                <div className="space-y-3">
                  {workflow.steps.map((step, idx) => (
                    <div key={idx} className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold mr-3">
                        {idx + 1}
                      </div>
                      <p className="text-sm text-gray-700 pt-1">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Key Concepts Section */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Key System Concepts</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Auto-Generated Numbers</h3>
              <ul className="space-y-3 text-sm text-gray-700">
                <li>
                  <strong>Customer Number:</strong> CUST001, CUST002, etc. - Unique identifier for each customer
                </li>
                <li>
                  <strong>GIN:</strong> Garage Identification Number - Unique identifier for vehicles
                </li>
                <li>
                  <strong>Job Card Numbers:</strong> JC2024001, JC2024002, etc. - Unique identifier for each job
                </li>
                <li>
                  <strong>DQ Numbers:</strong> DQ2024001, DQ2024002, etc. - Diagnostic questionnaire identifier
                </li>
                <li>
                  <strong>Invoice Numbers:</strong> Auto-generated sequential numbering
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Photo Management</h3>
              <p className="text-sm text-gray-700 mb-3">
                Multi-tier storage strategy with automatic resizing:
              </p>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span><strong>Original:</strong> Full resolution for documentation</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span><strong>Medium:</strong> 800x800px for viewing</span>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-600 mr-2">•</span>
                  <span><strong>Thumb:</strong> 200x200px for galleries</span>
                </li>
              </ul>
              <p className="text-sm text-green-600 font-medium mt-3">
                Saves 70-85% on storage and bandwidth
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Data Flow</h3>
              <div className="space-y-2 text-sm text-gray-700">
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-semibold mr-2">1</div>
                  <span>Customer created with Customer Number</span>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-semibold mr-2">2</div>
                  <span>Vehicle registered with GIN</span>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-semibold mr-2">3</div>
                  <span>Job Card created for Vehicle</span>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-semibold mr-2">4</div>
                  <span>Tasks assigned to Mechanics</span>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-semibold mr-2">5</div>
                  <span>Procurement adds cost prices</span>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-semibold mr-2">6</div>
                  <span>Finance creates Quotation</span>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-semibold mr-2">7</div>
                  <span>Mechanics complete Tasks</span>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-semibold mr-2">8</div>
                  <span>Finance generates Invoice</span>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-semibold mr-2">9</div>
                  <span>Customer makes Payment</span>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-semibold mr-2">10</div>
                  <span>Service Advisor closes Job</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Best Practices</h3>
              <div className="space-y-3">
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-1">Data Quality</h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      <span>Enter complete information</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      <span>Verify accuracy before submitting</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      <span>Don't create duplicate records</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-1">Security</h4>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      <span>Protect your login credentials</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      <span>Log out when leaving workstation</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      <span>Respect customer privacy</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Ready to Learn More?
          </h2>
          <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
            Explore our comprehensive role-based guides for detailed step-by-step instructions,
            daily workflows, and best practices.
          </p>
          <button
            onClick={() => navigate('/help')}
            className="inline-flex items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            Browse All Guides
            <ArrowRightIcon className="ml-2 h-5 w-5" />
          </button>
        </section>
      </div>
    </div>
  );
};

export default SystemOverview;
